import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import BrandMark from "@/components/BrandMark";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    const r = await login(email, password);
    setLoading(false);
    if (r.success) {
      toast.success("Welcome back.");
      nav(loc.state?.from || "/dashboard");
    } else {
      setError(r.error);
    }
  }

  return (
    <div className="min-h-screen bg-bone grid grid-cols-1 md:grid-cols-2">
      <div className="hidden md:block relative">
        <img
          src="https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxzdXN0YWluYWJpbGl0eSUyMHRleHR1cmUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4MTUwMjMzOHww&ixlib=rb-4.1.0&q=85"
          alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-forest/60" />
        <div className="relative h-full p-12 flex flex-col justify-between text-bone">
          <Link to="/" data-testid="login-logo">
            <BrandMark variant="dark" size="md" />
          </Link>
          <div>
            <p className="label-eyebrow text-bone/70">Quiet automation</p>
            <h2 className="font-serif text-5xl mt-3 leading-tight">Your home, listening for ways to save.</h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <p className="label-eyebrow mb-4">Welcome back</p>
          <h1 className="font-serif text-4xl md:text-5xl text-forest font-light tracking-tight">Sign in.</h1>
          <p className="mt-3 text-ink/65">Step back into your home's quiet metrics.</p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl bg-white border-[#E2DFD8] h-12" data-testid="login-email" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl bg-white border-[#E2DFD8] h-12" data-testid="login-password" />
            </div>
            {error && <p className="text-sm text-terracotta" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-forest hover:bg-forest-hover text-bone rounded-full h-12 text-base" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-[#E2DFD8]" />
            <span className="text-xs text-ink/50 font-mono uppercase tracking-widest">or</span>
            <div className="flex-1 h-px bg-[#E2DFD8]" />
          </div>

          <button
            type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="mt-4 w-full h-12 rounded-full border border-[#E2DFD8] bg-white hover:border-forest hover:text-forest text-ink flex items-center justify-center gap-3 transition-colors"
            data-testid="login-google-btn"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            <span className="text-sm font-medium">Continue with Google</span>
          </button>

          <p className="mt-8 text-sm text-ink/60">
            New to Noa? <Link to="/register" className="text-forest font-medium hover:underline" data-testid="login-to-register">Create account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
